import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  inject,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgClass } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { NAV_ITEMS, NavItem } from '../../../navigation/nav-items';
import { SidebarFlyoutDirective } from './sidebar-flyout/sidebar-flyout.directive';
import { ProfileService } from '../../../core/auth/services/profile.service';
import { AuthService } from '../../../core/auth/services/auth.service';
import { TranslationManagerNavigationService } from '../../services/translation-manager-navigation.service';

@Component({
  selector: 'app-base-sidebar',
  standalone: true,
  imports: [RouterModule, NgClass, SidebarFlyoutDirective],
  templateUrl: './base-sidebar.component.html',
  styleUrl: './base-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseSidebarComponent implements OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private readonly translationManagerNavigationService = inject(
    TranslationManagerNavigationService,
  );
  readonly supportUrl =
    'https://aznextgen.service-now.com/esc?id=search&spa=1&q=az%20translate&disableAllSuggestions=false&search_application=7296910f53171010069addeeff7b12e7&search_results_configuration=9264a1c353571010069addeeff7b121e&disableSpellCheck=false';
  private destroyed = false;
  /** Custom logo URL for expanded sidebar (defaults to AstraZeneca logo) */
  @Input() logoUrl?: string;
  /** Custom compact logo URL for collapsed sidebar (defaults to AZ logo) */
  @Input() logoCompactUrl?: string;
  /** Custom logo URL for bottom section when expanded */
  @Input() bottomLogoUrl?: string;
  /** Custom compact logo URL for bottom section when collapsed */
  @Input() bottomLogoCompactUrl?: string;
  /** Company name (defaults to 'AstraZeneca') */
  @Input() companyName: string = 'AstraZeneca';
  /** Product tagline (defaults to 'Binding Corporate Rules') */
  @Input() tagline: string = 'Binding Corporate Rules';
  /** Short version of tagline (defaults to 'BCR') */
  @Input() taglineShort: string = 'BCR';

  private _isCollapsed = true; // Start collapsed by default
  get isCollapsed(): boolean {
    return this._isCollapsed;
  }
  set isCollapsed(value: boolean) {
    if (this._isCollapsed !== value) {
      this._isCollapsed = value;
      this.cdr.markForCheck();
    }
  }
  isHome = false;
  canSeeAdmin = false;
  navItems: NavItem[] = [];
  private routerSub?: Subscription;
  private profileSub?: Subscription;
  private authSub?: Subscription;
  private userOverride = false;
  private readonly BREAKPOINT = 1280;
  private profileRequested = false;

  // Default logo paths - AZ Translate branded logos
  readonly defaultLogoUrl = '/assets/translate-logo-not-collapsed.svg';
  readonly defaultLogoCompactUrl = '/assets/translate-logo-collapsed.svg';
  // Default bottom logo paths
  readonly defaultBottomLogoUrl = '/assets/white-logo.svg';
  readonly defaultBottomLogoCompactUrl = '/assets/az-only-logo.svg';

  constructor(
    private router: Router,
    private profileService: ProfileService,
    private authService: AuthService,
  ) {
    // Set initial collapsed state - default to collapsed for better mobile experience
    try {
      if (typeof window !== 'undefined') {
        this.isCollapsed = true; // Always start collapsed
      }
    } catch {
      // ignore (SSR / tests without window)
    }
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((ev) => {
        const seg = ev.urlAfterRedirects.replace(/^\//, '').split('/')[0] || '';
        this.scheduleHomeUpdate(seg === 'home' || seg === '');
      });
    this.updateNavItems(false);
    this.authSub = this.authService.currentUser$.subscribe((user) => {
      if (!user) {
        this.profileRequested = false;
        this.profileSub?.unsubscribe();
        this.profileSub = undefined;
        this.scheduleNavUpdate(false);
        return;
      }

      if (this.profileRequested) {
        return;
      }

      this.profileRequested = true;
      this.profileSub?.unsubscribe();
      this.profileSub = this.profileService.getProfile(true).subscribe({
        next: (profile) => this.scheduleNavUpdate(profile?.isAdmin ?? false),
        error: () => {
          this.profileRequested = false;
          this.scheduleNavUpdate(false);
        },
      });
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.routerSub?.unsubscribe();
    this.profileSub?.unsubscribe();
    this.authSub?.unsubscribe();
  }

  private scheduleNavUpdate(isAdmin: boolean): void {
    queueMicrotask(() => {
      if (this.destroyed) return;
      this.updateNavItems(isAdmin);
      this.cdr.markForCheck();
    });
  }

  private scheduleHomeUpdate(nextIsHome: boolean): void {
    queueMicrotask(() => {
      if (this.destroyed) return;
      this.isHome = nextIsHome;
      this.cdr.markForCheck();
    });
  }

  private updateNavItems(isAdmin: boolean): void {
    this.canSeeAdmin = isAdmin;

    this.navItems = NAV_ITEMS.filter((i: NavItem) => {
      if (i.hide) return false;
      return true;
    });
  }

  goHome() {
    this.router.navigate(['/home']);
  }

  toggle() {
    this.userOverride = true;
    this.isCollapsed = !this.isCollapsed;
    this.userOverride = true; // prevent auto-resizing after manual toggle
  }

  openExternal(url: string): void {
    if (!url) {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  trackByNav = (_: number, item: NavItem) => item.route + '|' + item.label;

  onNavItemClick(item: NavItem, isActive: boolean): void {
    const isTranslationManager = item.route === '/translation-manager';
    const isWithinTranslationManagerRoute = this.router.url.startsWith(
      '/translation-manager',
    );

    if (isTranslationManager && (isActive || isWithinTranslationManagerRoute)) {
      this.translationManagerNavigationService.requestClearFilters();
    }
  }

  getViewportWidth(): number {
    try {
      return typeof window !== 'undefined'
        ? window.innerWidth
        : this.BREAKPOINT + 1; // default to expanded
    } catch {
      return this.BREAKPOINT + 1;
    }
  }

  @HostListener('window:resize')
  onResize() {
    if (this.userOverride) return;
    const shouldCollapse = this.getViewportWidth() < this.BREAKPOINT;
    if (shouldCollapse !== this.isCollapsed) {
      this.isCollapsed = shouldCollapse;
    }
  }
}
